/**
 * Custom hook for admin data fetching operations.
 * Handles master sheet and table data loading with caching.
 */
import { useState, useCallback, useRef } from 'react';
import type { TableRow } from '../types';

interface UseAdminDataProps {
  subreddit: string | null;
  /** When set (e.g. parent sub for a subscriber), load master_sheet for this sub_name instead of subreddit. */
  masterSheetSourceSub?: string | null;
  user: { username: string } | null;
  setMessage: (msg: { type: 'success' | 'error' | 'warning'; text: string; details?: string } | null) => void;
}

export function useAdminData({ subreddit, masterSheetSourceSub, user, setMessage }: UseAdminDataProps) {
  const [masterSheetData, setMasterSheetData] = useState<any[]>([]);
  const [isLoadingMasterSheet, setIsLoadingMasterSheet] = useState(false);
  const [masterSheetCacheKey, setMasterSheetCacheKey] = useState<string | null>(null);
  
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [tableDataCache, setTableDataCache] = useState<{ [key: string]: TableRow[] }>({});
  
  // Refs to prevent concurrent loads
  const masterSheetCacheKeyRef = useRef<string | null>(null);
  const masterSheetDataLengthRef = useRef<number>(0);
  const usernameRef = useRef<string | null>(null);
  const isLoadingMasterSheetRef = useRef<boolean>(false);

  // Sync refs with state
  const syncRefs = useCallback(() => {
    masterSheetCacheKeyRef.current = masterSheetCacheKey;
    masterSheetDataLengthRef.current = masterSheetData.length;
    usernameRef.current = user?.username || null;
  }, [masterSheetCacheKey, masterSheetData.length, user?.username]);

  const loadMasterSheet = useCallback(async (forceReload = false, quickLoad = false) => {
    if (!subreddit) return;

    const fetchSub = (masterSheetSourceSub || subreddit).toLowerCase();
    
    const currentCacheKey = masterSheetCacheKeyRef.current;
    const currentDataLength = masterSheetDataLengthRef.current;
    const username = usernameRef.current;
    
    if (!username) return;
    
    if (isLoadingMasterSheetRef.current && !forceReload) return;
    
    if (!forceReload && currentCacheKey === fetchSub && currentDataLength > 0) {
      return;
    }
    
    const hasCachedData = currentCacheKey === fetchSub && currentDataLength > 0;
    
    if (currentCacheKey !== fetchSub) {
      setMasterSheetData([]);
      masterSheetDataLengthRef.current = 0;
    }
    
    if (!hasCachedData) {
      setIsLoadingMasterSheet(true);
      isLoadingMasterSheetRef.current = true;
    }
    
    try {
      const { API_BASE_URL } = await import('../../../config');
      const url = quickLoad 
        ? `${API_BASE_URL}/admin/master-sheet/${fetchSub}?limit=200`
        : `${API_BASE_URL}/admin/master-sheet/${fetchSub}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const rows = data.rows || [];
        
        if (quickLoad && rows.length === 200) {
          setMasterSheetData(rows);
          setMasterSheetCacheKey(fetchSub);
          masterSheetCacheKeyRef.current = fetchSub;
          masterSheetDataLengthRef.current = rows.length;
          setIsLoadingMasterSheet(false);
          isLoadingMasterSheetRef.current = false;
          
          // Load rest in background
          const fullResponse = await fetch(`${API_BASE_URL}/admin/master-sheet/${fetchSub}`, {
            headers: {
              'Authorization': `Bearer ${username}`,
            },
          });
          if (fullResponse.ok) {
            const fullData = await fullResponse.json();
            const fullRows = fullData.rows || [];
            setMasterSheetData(fullRows);
            masterSheetDataLengthRef.current = fullRows.length;
          }
        } else {
          setMasterSheetData(rows);
          setMasterSheetCacheKey(fetchSub);
          masterSheetCacheKeyRef.current = fetchSub;
          masterSheetDataLengthRef.current = rows.length;
          setIsLoadingMasterSheet(false);
          isLoadingMasterSheetRef.current = false;
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: `Failed to load flair sheet: ${errorData.error || response.statusText}` });
        setIsLoadingMasterSheet(false);
        isLoadingMasterSheetRef.current = false;
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error loading flair sheet: ${error instanceof Error ? error.message : 'Unknown error'}` });
      setIsLoadingMasterSheet(false);
      isLoadingMasterSheetRef.current = false;
    }
  }, [subreddit, masterSheetSourceSub, setMessage]);

  const loadTableData = useCallback(async (
    tableName: string,
    filters?: { class?: string; family?: string; genus?: string; flair?: string },
    forceReload = false
  ) => {
    if (!subreddit || !user?.username) return;
    
    const cacheKey = `${subreddit}_${tableName}_${JSON.stringify(filters || {})}`;
    
    if (!forceReload && tableDataCache[cacheKey] && tableDataCache[cacheKey].length > 0) {
      setTableData(tableDataCache[cacheKey]);
      return;
    }
    
    setIsLoadingTable(true);
    if (!tableDataCache[cacheKey] || tableDataCache[cacheKey].length === 0) {
      setTableData([]);
    }
    
    try {
      const { API_BASE_URL } = await import('../../../config');
      const params = new URLSearchParams({ subreddit });
      if (filters?.class) params.append('class', filters.class);
      if (filters?.family) params.append('family', filters.family);
      if (filters?.genus) params.append('genus', filters.genus);
      if (filters?.flair) params.append('flair', filters.flair);
      
      const response = await fetch(`${API_BASE_URL}/admin/tables/${tableName}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const rows = data.rows || [];
        setTableData(rows);
        setTableDataCache(prev => ({ ...prev, [cacheKey]: rows }));
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: `Failed to load ${tableName}: ${errorData.error || response.statusText}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error loading ${tableName}: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoadingTable(false);
    }
  }, [subreddit, user?.username, tableDataCache, setMessage]);

  return {
    masterSheetData,
    isLoadingMasterSheet,
    masterSheetCacheKey,
    masterSheetCacheKeyRef,
    masterSheetDataLengthRef,
    isLoadingMasterSheetRef,
    usernameRef,
    setMasterSheetData,
    setMasterSheetCacheKey,
    loadMasterSheet,
    tableData,
    isLoadingTable,
    tableDataCache,
    setTableData,
    setTableDataCache,
    loadTableData,
    syncRefs,
  };
}
